import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { axiosClient } from '@/libs/axios-client';
import { useTimeTrackingStore } from './useTimeTrackingStore';

/**
 * Zustand store for managing and persisting the authentication state.
 *
 * This store uses Zustand's `persist` middleware to save the authentication state
 * in localStorage and `devtools` middleware for easy debugging during development.
 *
 */
export const useAuthStore = create(
  devtools(
    persist(
      (set, get) => ({
        isLoading: false,
        user: null,
        error: null,

        // * Login handler
        login: async (payload) => {
          set({ isLoading: true, error: null });

          try {
            const response = await axiosClient.post('/auth/login', payload);
            const { data } = response;

            // A challenged sign-in has no session yet. Storing the user here
            // would let `withPrivateRoute` wave the person through before they
            // ever entered the code.
            if (data?.mfaRequired) {
              set({ isLoading: false });

              return response;
            }

            // Same reason as in `resetAuth`: a session that ended without a
            // clean sign-out (expiry, closed tab) leaves the previous account's
            // company behind for whoever signs in next.
            useTimeTrackingStore.getState().reset();

            set({
              user: {
                ...data,
              },
              isLoading: false,
            });

            return response;
          } catch (err) {
            console.error('Login error:', err);

            set({
              user: null,
              isLoading: false,
              error: 'Login failed',
            });

            throw err;
          }
        },

        // * Logout handler
        logout: async () => {
          set({ isLoading: true, error: null });

          try {
            const response = await axiosClient.post('/auth/logout');
            get().resetAuth();
            set({ isLoading: false });

            return response;
          } catch (err) {
            console.error('Logout error:', err);
            set({ isLoading: false, error: 'Logout failed' });

            throw err;
          }
        },

        // * Reset handler
        resetAuth: () => {
          set({ user: null, isLoading: false, error: null });

          // The time tracking store keeps the picked company in localStorage,
          // which is shared by every account that signs in from this browser.
          // Left behind, the next person inherits a company they may not open:
          // the picker cannot label it, so it shows a bare ObjectId, and the
          // request behind it is refused.
          useTimeTrackingStore.getState().reset();
        },

        // * Set user data directly (optional if needed in login)
        setUserData: (userData) => {
          set({ user: userData });
        },
      }),
      {
        name: 'auth-storage', // Storage name
      }
    ),
    {
      name: 'AuthStore', // Devtools name
    }
  )
);
